/**
 * The privacy policy and terms, as data.
 *
 * WHY THEY EXIST: Google Play will not accept an app without a reachable privacy-policy URL, and
 * CherryClipz now takes other people's email addresses and video, so it needs one regardless.
 *
 * HONESTY RULE, same as everywhere else in this repo: every statement below describes something
 * the code actually does. The processor list is the real one — AssemblyAI (transcription), AWS
 * (S3 storage, us-east-2), Render (hosting, Ohio), SendGrid (all Django email, settings.py),
 * Stripe (payments), Sentry (crash reports), Contentsquare (the tag in public/index.html), Google
 * (optional sign-in), the user's own Auto webhook (transcript/auto_webhook.py). If a processor is
 * added or dropped, change this file in the same commit.
 *
 * WHO "WE" ARE: Emo Transcripts LLC (New York) owns CherryClipz. OpenAI's plugin review rejected
 * the ChatGPT app on 2026-10-28 because nothing public tied the brand to the verified business,
 * so both pages say it up front, and the static copies say it in the footer too.
 *
 * STAFF ACCESS + MODERATION (2026-10-31, Isaac's call): staff may open any upload to help, to make
 * clips/edits/AI versions for the owner, and to check it follows the terms; content that breaks
 * them can be taken down. Staff never publish a user's content. Only opening the clip editor is
 * logged (admin "Open ET"), so the privacy text promises a record for that and nothing more.
 *
 * This is a careful draft, not legal advice. A lawyer should read it before launch.
 */

export const LEGAL_UPDATED = "31 October 2026";

export const LEGAL_COMPANY = "Emo Transcripts LLC";
export const LEGAL_COMPANY_LINE = `CherryClipz is made and run by ${LEGAL_COMPANY}, New York, USA.`;

// Site footers (home + every BrandFooter page, incl. /contact = the plugin's Support URL).
export const LEGAL_FOOTER_OWNER = `© CherryClipz · ${LEGAL_COMPANY}`;
// The home page shows the OpenAI and Claude marks; OpenAI's brand rules ask that a logo be
// credited as theirs and not suggest endorsement.
export const LEGAL_TRADEMARKS =
  "ChatGPT and OpenAI are trademarks of OpenAI, and Claude is a trademark of Anthropic. Other product names and logos belong to their owners and appear only to show what CherryClipz works with. CherryClipz is not made or endorsed by any of them.";

export const PRIVACY_TITLE = "Privacy policy";
export const PRIVACY_INTRO =
  "CherryClipz turns a video into a searchable spreadsheet of clips. To do that we hold your email address, the files you choose to upload, and the transcripts made from them. This page says exactly what we keep, who else sees it, and how to get rid of it.";

export const TERMS_TITLE = "Terms of service";
export const TERMS_INTRO =
  "These terms cover the CherryClipz website, the iPhone and Android apps, the Premiere Pro panel, and using CherryClipz from an AI assistant such as Claude or ChatGPT. Using any of them means you accept what is written here.";

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    heading: "Who we are",
    paragraphs: [
      `CherryClipz is made and run by ${LEGAL_COMPANY}, a company registered in New York, USA. In this policy, "we" and "us" mean ${LEGAL_COMPANY}.`,
    ],
  },
  {
    heading: "What we collect",
    bullets: [
      "Your email address. It is how you sign in; there is no password to lose.",
      "Video and audio files you choose to upload, and anything derived from them: the transcript, the timings, who spoke, the mood of each line, and the clips you export.",
      "Videos you send us as a link instead of a file, for example from YouTube, Google Drive or Dropbox. We download the video from that link and treat it like an upload.",
      // My Library → Meetings: the Apps Script in constants/meetScript.ts POSTs file_id, name and
      // calendar guests to /meetings/drive/<token>/; Drive imports email only the owner.
      "Google Meet recordings, if you add our Meet script to your own Google account. It finds new recordings in your Google Drive, shares each one by link so we can download it, and sends us the meeting's name and the names and email addresses of its guests from your calendar, so your clips can show who was there. We do not email those guests.",
      "Basic account records: when you signed up, which plan you are on, how many hours you have transcribed this month, and how much storage you are using.",
      "Payment records if you subscribe. Card details go straight to Stripe and never reach our servers.",
      "Ordinary technical logs, including your browser type and network address, kept so we can find faults and stop abuse.",
    ],
  },
  {
    heading: "What the phone app does differently",
    paragraphs: [
      "The phone app, on iPhone and Android, can cut a video into clips entirely on your phone, including listening to the speech, and that works with no account and in airplane mode. Nothing about those videos reaches us.",
      "A file leaves your phone only when you choose CherryClipz AI for a better transcript. Until you tap that, we never see it.",
    ],
  },
  {
    heading: "Who else handles your files",
    paragraphs: [
      "We use a small number of companies to run the service. They only ever get what they need for their part of it.",
    ],
    bullets: [
      "AssemblyAI transcribes uploaded audio and returns the words, speakers and timings.",
      "Amazon Web Services stores your files, in the US East (Ohio) region, under a folder belonging to your account alone.",
      "Render hosts the website, the API and the background worker.",
      "SendGrid sends our emails, such as your sign-in codes.",
      "Stripe takes payments and holds the card details.",
      "Sentry receives crash reports so we can fix errors.",
      "Contentsquare records anonymous usage of the website so we can see which parts confuse people.",
      "Google, only if you choose to sign in with a Google account.",
      "AI services such as OpenAI, Anthropic or Google, only when our team uses one to make something for you, as described below, and on shared editor links.",
      "Your own Auto webhook, only if you set one up. We send it a link to each finished export, with its details and a suggested caption, and the service you connected, such as make.com, then handles them under its own terms.",
    ],
  },
  {
    heading: "When our team looks at your videos",
    paragraphs: [
      "People on the CherryClipz team can see what you upload: the video and audio files, their transcripts, and the clips made from them. Only staff accounts can do this. We look for three reasons: to help you, for example when you ask or when an upload goes wrong; to make clips, edits, captions or other versions of your video for you; and to check that uploads follow our terms of service.",
      "Every time one of us opens the clip editor for one of your videos, we record who opened it and when.",
      "Anything we make for you is saved to your own library or sent to you as a link. To make it we may use AI tools such as ChatGPT or Claude, which then see the parts of the transcript or video we give them.",
      "We can also make a share link to one of your videos so we can send it to you. It is the same kind of link you can make yourself: anyone who has it can open those clips without signing in. It appears with your other share links in your library, where you can turn it off, and we record that we made it.",
      "We do not publish your videos, post them anywhere, or give them to anyone else without your permission. The only exceptions are the companies listed above that run the service for us, and when the law requires it.",
      "If something you upload breaks our terms, for example sexual content or content that encourages violence, we may take it down and suspend the account, as the terms of service explain.",
    ],
  },
  {
    heading: "The AI features you run yourself",
    paragraphs: [
      "Black Cherry, the AI assistant inside the clip editor, uses an API key that you supply and that stays in your own browser. Those requests go straight from your browser to the provider you chose, such as OpenAI, Anthropic or Google. They never pass through our servers, and we never see that key or those conversations.",
      "The one exception is a shared editor link, where the AI runs on a key of ours and is capped per link.",
    ],
  },
  {
    // The MCP connector (emo/mcp_server/): OAuth sign-in on our /o/login/ page, the tools in
    // mcp_server/tools.py, tokens in oauth2_provider, last picks in LivePickSession.
    heading: "Using CherryClipz from Claude or ChatGPT",
    paragraphs: [
      "You can connect your CherryClipz account to an AI assistant such as Claude or ChatGPT. You sign in on our own page, and the assistant gets a token that works only for your account. Disconnect CherryClipz in the assistant's settings to stop it, or email us and we will cut off its access.",
      "When you ask it to, the assistant can list your videos, read a video's transcript (each line's words, speaker, mood and timing), tick clips in your open editor, export clips to your library, import a video from a link, and make a share link. If you have set an Auto webhook, exports it makes can go there too. We never send it your video files.",
      "What the assistant reads goes to the company that runs it, such as Anthropic or OpenAI, and is handled under that company's privacy policy. On our side we keep the sign-in tokens it uses and the clips it last picked for each video, so your editor can follow along.",
    ],
  },
  {
    heading: "We do not sell your data",
    paragraphs: [
      "We do not sell or rent personal information, and we do not use your videos to train any AI model of our own.",
    ],
  },
  {
    heading: "How long we keep things",
    paragraphs: [
      "Files and transcripts stay until you delete them, you close your account, or we delete them as our terms of service allow. Delete a video in your library and it goes from our storage too. Billing records are kept as long as tax law requires.",
    ],
  },
  {
    heading: "Your choices",
    bullets: [
      "Delete any video, transcript or export from your library at any time.",
      "Ask us to close your account and remove everything, by email.",
      "Ask for a copy of what we hold about you.",
      "Use the phone app with no account at all, in which case we hold nothing.",
    ],
  },
  {
    heading: "Children",
    paragraphs: [
      "CherryClipz is not intended for children under 13, and we do not knowingly keep their information.",
    ],
  },
  {
    heading: "Changes",
    paragraphs: [
      "If this policy changes in a way that matters, we will say so on this page and update the date at the top.",
    ],
  },
];

export const TERMS_SECTIONS: LegalSection[] = [
  {
    heading: "Who we are",
    paragraphs: [
      `CherryClipz is made and run by ${LEGAL_COMPANY}, a company registered in New York, USA. These terms are an agreement between you and ${LEGAL_COMPANY}; "we" and "us" mean that company.`,
    ],
  },
  {
    heading: "Your account",
    paragraphs: [
      "You need an email address you control. You are responsible for what happens under your account, so tell us if you think someone else has got into it.",
    ],
  },
  {
    heading: "Your content stays yours",
    paragraphs: [
      "You keep every right in the videos you upload and the clips you make. You give us permission to use your content only to run CherryClipz for you: to store your files, send the audio for transcription, make the clips and exports you ask for, and send them where you tell us to, such as a share link or an Auto webhook.",
      "You also allow our team to open what you upload, to help you, to make clips, edits, captions or AI-made versions of it for you, and to check that it follows these terms. Anything we make for you goes to your library or to you as a link. We will not publish your content or give it to anyone else without your permission, apart from the companies that run the service for us and when the law requires it.",
      "This permission ends when you delete the content or close your account.",
      "You confirm that you have the right to upload what you upload, including any permission the law requires from the people who appear or speak in it.",
    ],
  },
  {
    heading: "Plans and hours",
    paragraphs: [
      "The free plan includes one hour of transcription in total, to try the service; it does not renew. Paid plans include an amount of transcription per month, plus storage and a number of exports. When you reach a limit, work you have already done keeps working; new transcriptions wait for a higher plan, or on a paid plan for the next month.",
      "Paid plans renew monthly until cancelled. You can cancel at any time from your subscription page and keep the plan until the period you paid for ends. We do not refund part-months.",
      "The phone app never charges anything, on iPhone or Android. Any payment happens on this website.",
    ],
  },
  {
    heading: "What you must not upload",
    paragraphs: ["Do not upload, import or share anything that is:"],
    bullets: [
      "Unlawful, or that you have no right to use, such as someone else's film, show or music without their permission.",
      "Pornographic or sexually explicit.",
      "Sexual in any way involving anyone under 18. We remove it, close the account and report it to the authorities.",
      "Encouraging or threatening violence, terrorism or self-harm. News, sport and documentary footage that shows violence is fine; content that promotes it is not.",
      "Hateful or harassing towards people for who they are, such as their race, religion, sex or disability.",
      "Someone else's private information, or a recording of people made without the consent the law requires.",
    ],
  },
  {
    heading: "What you must not do",
    bullets: [
      "Try to break, overload or get around the limits of the service.",
      "Resell the service as your own.",
    ],
  },
  {
    heading: "Checking and removing content",
    paragraphs: [
      "We may look at anything uploaded to CherryClipz to check that it follows these terms. If we believe something breaks them, or puts people or the service at risk, we may remove it and suspend or close the account, with or without warning first.",
      "We do not check everything and cannot promise to catch everything. If you see something that breaks these rules, or think we removed something of yours by mistake, email us and a person will look at it.",
    ],
  },
  {
    heading: "Deleting content",
    paragraphs: [
      "We may delete any content, or close any account, at any time and for any reason, for example to remove accounts that have not been used for a long time or if we stop running CherryClipz. When we can, we will email you first so you have time to download what you want to keep.",
    ],
  },
  {
    heading: "Copyright complaints",
    paragraphs: [
      "If you believe something on CherryClipz uses your work without permission, email us with what your work is, where it appears on CherryClipz (a share link is best), your name and contact details, and a statement that you believe in good faith the use is not allowed. We will look at it, remove the content where that is right, and close the accounts of people who do it again and again.",
    ],
  },
  {
    heading: "Things we do not promise",
    paragraphs: [
      "Transcription is done by machine and will sometimes be wrong. Check anything that matters before you rely on it.",
      "We aim to keep the service running and your files safe, but we cannot promise it will never be unavailable or that nothing will ever be lost. Keep your own copy of anything you cannot afford to lose.",
    ],
  },
  {
    heading: "Ending it",
    paragraphs: [
      "You can stop using CherryClipz whenever you like and ask us to delete your account. We may suspend or close an account that breaks these terms or puts the service at risk.",
    ],
  },
  {
    heading: "Changes to these terms",
    paragraphs: [
      "We may update these terms as CherryClipz changes. If a change matters, we will say so on this page and update the date at the top. Using CherryClipz after that means you accept the new version.",
    ],
  },
  {
    heading: "The law that applies",
    paragraphs: ["These terms are governed by the laws of the State of New York, USA."],
  },
  {
    heading: "Getting in touch",
    paragraphs: [
      "Questions about these terms, your data, or anything else go to the address below and reach a person.",
    ],
  },
];
